import React, { useEffect, useRef, useState } from 'react';
import T from 'i18n';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey,
  faUserLock,
  faStickyNote,
  faLink,
  faInfoCircle,
  faLayerGroup,
  faCircle,
  faRetweet,
  faFlag,
  faCut,
  faMinusCircle,
  faEdit,
  faDownload,
  faExternalLinkAlt,
  faTrashAlt,
  faBookmark,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InfoTwoRows from 'features/common/InfoTwoRows';
import PopoverStickOnHover from 'features/common/PopoverStickOnHover';
import Popper from '@mui/material/Popper';
import { FileDetails } from '../FileDetails';
import { BundleLocations } from '../Viewer';
import { FileAttachments } from '..';
import { ClickAwayListener } from '@mui/material';
import FilterProvider from './FilterProvider';
import { confidentialityRingCategoryLabel, documentModalActions } from '../enums';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConfidentialityRing,
  selectCurrentUserConfidentialityRing,
  selectPermissions,
  selectFilePipelineStatusByFileId,
  selectFilePipelineStatusPendingByFileId,
  selectFilePipelineStatusErrorByFileId,
  selectMatchedUrl,
  selectShowExhibitNumber,
  selectEnableRemovePages,
  selectCanUpdateExhibitReferences,
} from 'common/selectors';
import { AttachEmail, Attachment, FilterCenterFocus } from '@mui/icons-material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import { fetchFilePipelineStatus } from '../redux/fetchFilePipelineStatus';
import { setFileAiReady } from '../redux/setFileAiReady';
import { useFetchNotifications } from '../redux/fetchNotifications';
export const cfRingColorMapping = {
  noRing: 'rgb(67 67 219)',
  outerRing: 'rgb(76, 175, 80)',
  innerRing: 'red',
} as any;

let aiNotReadyStylesInjected = false;

const ensureAiNotReadyStyles = () => {
  if (aiNotReadyStylesInjected) {
    return;
  }

  if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.id = 'ai-not-ready-dots-keyframes';
    style.textContent = `
      @keyframes aiNotReadyDotFade {
        0%, 70%, 100% { opacity: 0.25; }
        35% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    aiNotReadyStylesInjected = true;
  }
};

const AiNotReadyDots = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ style: incomingStyle, ...rest }, ref) => {
    useEffect(() => {
      ensureAiNotReadyStyles();
    }, []);

    const dotStyle: React.CSSProperties = {
      marginTop: '0.5rem',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: '#EF8E43',
      opacity: 0.25,
      animation: 'aiNotReadyDotFade 1.2s infinite ease-in-out',
      display: 'inline-block',
    };

    const delays = ['0s', '0.2s', '0.4s'];

    return (
      <div
        ref={ref}
        {...rest}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '3px',
          width: '24px',
          height: '16px',
          margin: '0 auto',
          ...incomingStyle,
        }}
      >
        {delays.map((delay) => (
          <span
            key={delay}
            style={{
              ...dotStyle,
              animationDelay: delay,
            }}
          />
        ))}
      </div>
    );
  },
);

AiNotReadyDots.displayName = 'AiNotReadyDots';

const GreenTick = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ style: incomingStyle, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        {...rest}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '16px',
          margin: '0 auto',
          ...incomingStyle,
        }}
      >
        <span
          style={{
            fontSize: '16px',
            color: '#2f855a',
            fontWeight: 'bold',
            lineHeight: '1',
          }}
        >
          ✓
        </span>
      </div>
    );
  },
);

GreenTick.displayName = 'GreenTick';

const SmallGreenTick = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ style: incomingStyle, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        {...rest}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '16px',
          margin: '0 auto',
          ...incomingStyle,
        }}
      >
        <span
          style={{
            fontSize: '12px',
            color: '#2f855a',
            fontWeight: 'bold',
            lineHeight: '1',
          }}
        >
          ✓
        </span>
      </div>
    );
  },
);

SmallGreenTick.displayName = 'SmallGreenTick';

// Memoize the component to prevent unnecessary re-renders with many files
const TableInfoMemoized = React.memo(
  ({
    row,
    customColumns,
    filter,
    present,
    isPresentModePage,
  }: any) => {
    const currrentUserCR = useSelector(selectCurrentUserConfidentialityRing);
    const permissions = useSelector(selectPermissions);
    const viewFlags = permissions?.documents?.viewFlags;
    const isConfidentialityRing =
      useSelector(selectConfidentialityRing) && currrentUserCR !== 'noRing';
    const [showAttachments, setShowAttachments] = useState(false);
    const [attachmentPopperPosition, setAttachmentPopperPosition] = useState(null);
    const [attachmentSource, setAttachmentSource] = useState<'attachments' | 'isAttachedTo' | null>(
      null,
    );
    const [showAiNotReadyPopover, setShowAiNotReadyPopover] = useState(false);
    const [showGreenTick, setShowGreenTick] = useState(false);
    const [showTransientTick, setShowTransientTick] = useState(false);
    const aiPopoverHoverRef = useRef(false);
    const transientTickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const processingTransientRef = useRef(false);
    const dispatch = useDispatch();
    const {
      aiNotReady,
      id,
      folderFileId,
      notes,
      assignedTo,
      hasAnnotations,
      hasPublicHyperlinks,
      hasCrossRefs,
      source,
      duplicate,
      locations,
      attachments,
      isAttachedTo,
      pageCount,
      confidentialityRing,
      flags,
    } = row;

    const { transientNotifications } = useFetchNotifications();
    const renderAiStepStatus = (complete: boolean) => (
      <span
        style={{
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.6rem',
          fontWeight: 600,
          border: complete ? 'none' : '1px solid #d1d5db',
          backgroundColor: complete ? '#2f855a' : 'transparent',
          color: complete ? '#fff' : '#adb5bd',
        }}
      >
        {complete ? '✓' : ''}
      </span>
    );
    const pipelineStatus = useSelector((state) => selectFilePipelineStatusByFileId(state, id));
    const pipelineStatusPending = useSelector((state) =>
      selectFilePipelineStatusPendingByFileId(state, id),
    );
    const pipelineStatusError = useSelector((state) =>
      selectFilePipelineStatusErrorByFileId(state, id),
    );
    const matchedUrl = useSelector(selectMatchedUrl);
    const pipelineSteps = Array.isArray(pipelineStatus) ? pipelineStatus : [];
    const completedAiSteps = pipelineSteps.filter(
      (step) => step.status && step.status.toLowerCase() === 'completed',
    ).length;
    const totalAiSteps = pipelineSteps.length;
    const progressPercent = totalAiSteps ? Math.round((completedAiSteps / totalAiSteps) * 100) : 0;
    const aiReadyStageCompleted = pipelineSteps.some((step) => {
      const rawName = (step.stageFriendlyName || step.stage || '').toLowerCase();
      const nameNoSpaces = rawName.replace(/\s+/g, '');
      const status = (step.status || '').toLowerCase();
      const isAiReadyStage = rawName.includes('ai ready') || nameNoSpaces.includes('aiready');
      return status === 'completed' && isAiReadyStage;
    });
    const allStepsCompleted =
      totalAiSteps > 0 &&
      pipelineSteps.every((step) => (step.status || '').toLowerCase() === 'completed');
    const isPipelineReady =
      !pipelineStatusPending && totalAiSteps > 0 && (aiReadyStageCompleted || allStepsCompleted);
    const [showMoreInfoIcon, setShowMoreInfoIcon] = useState(false);

    const isPrivate = !(
      assignedTo &&
      Array.isArray(assignedTo) &&
      (assignedTo.includes('trialbooks') ||
        assignedTo.includes('my-court-bundles') ||
        assignedTo.includes('other-court-bundles'))
    );
    const isRecycleBin =
      (!document as any)?.recycleBin ||
      (!document as any)?.bundleLocations?.length === 0 ||
      (document as any)?.isRecycleBin ||
      (document as any)?.recycleBin ||
      (matchedUrl && matchedUrl.includes('recycle-bin'));
    const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
    const hasAttachedTo = Array.isArray(isAttachedTo) && isAttachedTo.length > 0;
    const attachmentListForSource =
      attachmentSource === 'isAttachedTo' ? isAttachedTo : attachments;
    const activeAttachments = Array.isArray(attachmentListForSource) ? attachmentListForSource : [];
    const attachmentHeadingKey =
      attachmentSource === 'isAttachedTo' ? 'generic.parentEmail' : 'case.fileAttachments';

    const handleAttachmentIconClick = (source: 'attachments' | 'isAttachedTo') => (e: any) => {
      const outsideElem = document.getElementsByClassName('document-count');
      // added bcoz clickAwayListener is not getting triggered when clicking on another attachment icon
      if (outsideElem && outsideElem.length > 0) {
        (outsideElem[0] as unknown as any).click();
      }
      e.preventDefault();
      e.stopPropagation();
      setAttachmentSource(source);
      setShowAttachments(true);
      setAttachmentPopperPosition(e.currentTarget);
    };

    const handleClickAway = () => {
      setShowAttachments(false);
      setAttachmentSource(null);
      setAttachmentPopperPosition(null);
    };

    const handleAiPopoverToggle = (next: boolean) => {
      if (!next && aiPopoverHoverRef.current) {
        return;
      }
      setShowAiNotReadyPopover(next);
    };

    const handleAiPopoverMouseEnter = () => {
      aiPopoverHoverRef.current = true;
      setShowAiNotReadyPopover(true);
    };

    const handleAiPopoverMouseLeave = () => {
      aiPopoverHoverRef.current = false;
      setShowAiNotReadyPopover(false);
    };

    const handleAiTriggerMouseEnter = () => {
      setShowAiNotReadyPopover(true);
      if (id) {
        dispatch(fetchFilePipelineStatus({ fileId: id }));
      }
    };

    const handleAiTriggerMouseLeave = () => {
      if (!aiPopoverHoverRef.current) {
        setShowAiNotReadyPopover(false);
      }
    };

    useEffect(() => {
      if (!aiNotReady || !isPipelineReady) {
        return;
      }

      // Show green tick for 1.5 seconds, then close popover and update AI ready state
      if (showAiNotReadyPopover) {
        setShowGreenTick(true);
        setTimeout(() => {
          setShowGreenTick(false);
          setShowAiNotReadyPopover(false);
          aiPopoverHoverRef.current = false;
          dispatch(setFileAiReady({ fileId: id, folderFileId }));
        }, 1500);
      } else {
        // If popover is not showing, just update the state immediately
        dispatch(setFileAiReady({ fileId: id, folderFileId }));
      }
    }, [aiNotReady, isPipelineReady, showAiNotReadyPopover, dispatch, id, folderFileId]);

    // Listen for transient notifications about this file becoming AI ready
    useEffect(() => {
      if (!aiNotReady || !transientNotifications || !Array.isArray(transientNotifications)) {
        return;
      }

      // Prevent processing if already handling a transient notification
      if (processingTransientRef.current) {
        return;
      }

      // Check if there's a transient notification for this file
      const aiReadyNotification = transientNotifications.find(
        (notification) =>
          notification.type === 'ai-ready' &&
          notification.status === 'complete' &&
          (notification.entityId === id || notification.entityId === folderFileId),
      );

      if (aiReadyNotification) {
        // Mark as processing to prevent duplicate handling
        processingTransientRef.current = true;

        // Only log in development or limit logging to prevent console spam
        if (process.env.NODE_ENV === 'development') {
          console.log('AI ready notification found for file:', id);
        }

        // Clear any existing timeout to prevent memory leaks
        if (transientTickTimeoutRef.current) {
          clearTimeout(transientTickTimeoutRef.current);
        }

        // Show the transient tick animation
        setShowTransientTick(true);

        // If popover is currently showing, trigger the green tick animation
        if (showAiNotReadyPopover) {
          setShowGreenTick(true);
          transientTickTimeoutRef.current = setTimeout(() => {
            setShowGreenTick(false);
            setShowAiNotReadyPopover(false);
            aiPopoverHoverRef.current = false;
            dispatch(setFileAiReady({ fileId: id, folderFileId }));
            transientTickTimeoutRef.current = null;
            processingTransientRef.current = false;
          }, 1500);
        } else {
          // If popover is not showing, show the transient tick for 1.5 seconds then update state
          transientTickTimeoutRef.current = setTimeout(() => {
            setShowTransientTick(false);
            dispatch(setFileAiReady({ fileId: id, folderFileId }));
            transientTickTimeoutRef.current = null;
            processingTransientRef.current = false;
          }, 1500);
        }
      }
    }, [transientNotifications, aiNotReady, id, folderFileId, showAiNotReadyPopover, dispatch]);

    // Cleanup timeout on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        if (transientTickTimeoutRef.current) {
          clearTimeout(transientTickTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (filter) {
        const removeColumns = ['tab', 'index', 'info'];
        const columns = customColumns
          .filter((column: any) => !removeColumns.includes(column))
          .map((col: any) =>
            col === 'docDate'
              ? 'docDate'
              : col === 'pageCount'
                ? 'pageCount'
                : col === 'fileType'
                  ? 'type'
                  : col,
          );
        const matchedData = columns.filter(
          (col: any) =>
            row[col] &&
            (col === 'docDate' ? moment(row[col], 'DD/MM/YYYY').format('DD/MM/YYYY') : row[col])
              .toString()
              .toLowerCase()
              .includes(filter.toLowerCase()),
        );
        if (matchedData.length > 0) setShowMoreInfoIcon(false);
        else setShowMoreInfoIcon(true);
      }
    }, [filter, customColumns, row]);

    return (
      <Row style={{ width: '100%', marginRight: '-2px', marginLeft: '-2px' }}>
        {isConfidentialityRing && confidentialityRing && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover
                  id="confidentialityRing"
                  style={{ maxWidth: '350px', fontSize: '0.75rem' }}
                >
                  <React.Fragment>
                    <Popover.Content>
                      {(confidentialityRingCategoryLabel as any)[confidentialityRing]}
                    </Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon
                style={{ color: cfRingColorMapping[confidentialityRing] }}
                icon={faCircle}
              />
            </OverlayTrigger>
          </Col>
        )}
        {aiNotReady && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              trigger="hover"
              placement="left"
              delay={{ show: 250, hide: 200 }}
              show={showAiNotReadyPopover}
              onToggle={handleAiPopoverToggle}
              overlay={
                <Popover
                  id="aiNotReady"
                  style={{ maxWidth: '350px', fontSize: '0.75rem' }}
                  onMouseEnter={handleAiPopoverMouseEnter}
                  onMouseLeave={handleAiPopoverMouseLeave}
                >
                  <React.Fragment>
                    <Popover.Content>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.6rem',
                          minWidth: '220px',
                          fontSize: '0.75rem',
                        }}
                      >
                        {showGreenTick ? (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem',
                              padding: '1rem 0',
                            }}
                          >
                            <div
                              style={{
                                fontSize: '2rem',
                                color: '#2f855a',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                            <span style={{ color: '#2f855a', fontWeight: 600 }}>
                              AI Processing Complete!
                            </span>
                          </div>
                        ) : (
                          <>
                            <span>{T.translate('generic.aiNotReady')}</span>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.45rem',
                              }}
                            >
                              <span style={{ fontWeight: 600, color: '#212529' }}>
                                Data Pipeline Progress
                              </span>
                              {pipelineStatusPending ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: '0.5rem 0',
                                  }}
                                >
                                  <Spinner
                                    animation="border"
                                    role="status"
                                    size="sm"
                                    style={{
                                      color: '#EF8E43',
                                      width: '1.25rem',
                                      height: '1.25rem',
                                    }}
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </Spinner>
                                </div>
                              ) : (
                                <>
                                  {pipelineStatusError ? (
                                    <span style={{ color: '#d64545' }}>{pipelineStatusError}</span>
                                  ) : null}
                                  {totalAiSteps > 0 ? (
                                    <>
                                      {pipelineSteps.map((step) => {
                                        const isComplete =
                                          step.status && step.status.toLowerCase() === 'completed';
                                        return (
                                          <div
                                            key={`${step.stage || ''}-${step.stageFriendlyName || ''}`}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              color: '#495057',
                                            }}
                                          >
                                            <span>
                                              {step.stageFriendlyName ||
                                                step.stage ||
                                                T.translate('generic.unknown')}
                                            </span>
                                            {renderAiStepStatus(isComplete)}
                                          </div>
                                        );
                                      })}
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          color: '#495057',
                                          paddingTop: '0.2rem',
                                        }}
                                      >
                                        <span>{`Progress: ${completedAiSteps} / ${totalAiSteps} steps`}</span>
                                        <span style={{ fontWeight: 600 }}>{progressPercent}%</span>
                                      </div>
                                    </>
                                  ) : !pipelineStatusError ? (
                                    <span style={{ color: '#6c757d' }}>
                                      No pipeline activity yet.
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              {showGreenTick ? (
                <GreenTick />
              ) : showTransientTick ? (
                <SmallGreenTick />
              ) : (
                <AiNotReadyDots
                  onMouseEnter={handleAiTriggerMouseEnter}
                  onMouseLeave={handleAiTriggerMouseLeave}
                />
              )}
            </OverlayTrigger>
          </Col>
        )}
        {locations && locations.length > 0 && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <div style={{ height: '12px', width: '12px' }}>
              <BundleLocations
                filePageCount={pageCount}
                bundleLocations={[]}
                fileId={id}
                filterVal={filter}
                isGrid={true}
              />
            </div>
          </Col>
        )}
        {(hasAttachments || hasAttachedTo) && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <div style={{ display: 'contents' }}>
              {hasAttachments && (
                <Col
                  style={{
                    paddingRight: '2px',
                    paddingLeft: '2px',
                    marginRight: '0.5rem',
                    flexGrow: 0,
                  }}
                >
                  <div
                    className="attachmentIcon"
                    onClick={handleAttachmentIconClick('attachments')}
                  >
                    <AttachEmail sx={{ '&:hover': { cursor: 'pointer' } }} fontSize="small" />
                  </div>
                </Col>
              )}
              {hasAttachedTo && (
                <Col
                  style={{
                    paddingRight: '2px',
                    paddingLeft: '2px',
                    marginRight: '0.5rem',
                    flexGrow: 0,
                  }}
                >
                  <div
                    className="attachmentIcon"
                    onClick={handleAttachmentIconClick('isAttachedTo')}
                  >
                    <Attachment sx={{ color: '#6c757c', '&:hover': { cursor: 'pointer' } }} />
                  </div>
                </Col>
              )}
              {showAttachments && attachmentSource && activeAttachments.length > 0 && (
                <Popper
                  id={`simple-attachment-popover-${id}-${attachmentSource}`}
                  open={Boolean(showAttachments)}
                  anchorEl={attachmentPopperPosition}
                  disablePortal={true}
                  placement="bottom-end"
                  style={{ zIndex: 5 }}
                >
                  <FileAttachments
                    attachments={activeAttachments}
                    handleClickAway={handleClickAway}
                    parentFileId={id}
                    present={present}
                    headingKey={attachmentHeadingKey}
                  />
                </Popper>
              )}
            </div>
          </ClickAwayListener>
        )}
        {showMoreInfoIcon && !isPresentModePage && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <PopoverStickOnHover
              component={
                <div>
                  <Popover.Title as="h4">{T.translate('generic.public')}</Popover.Title>
                  <Popover.Content>
                    <FilterProvider filter={filter}>
                      <FileDetails row={row} columns={customColumns} DefaultShowMoreInfo={false} />
                    </FilterProvider>
                  </Popover.Content>
                </div>
              }
              placement="left"
              onMouseEnter={() => {}}
              delay={{ show: 250, hide: 400 }}
              style={{ width: '350px', maxWidth: '350px', fontSize: '0.75rem' }}
            >
              <div style={{ height: '12px', width: '12px' }}>
                <FontAwesomeIcon style={{ color: '#7BA0F4' }} icon={faInfoCircle} />
              </div>
            </PopoverStickOnHover>
          </Col>
        )}
        {notes && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover
                  id="notes"
                  style={{ width: '350px', maxWidth: '350px', fontSize: '0.75rem' }}
                >
                  <React.Fragment>
                    <Popover.Title as="h4">{T.translate('case.privateToTeam')}</Popover.Title>
                    <Popover.Content>
                      {notes && (
                        <FilterProvider filter={filter}>
                          <InfoTwoRows field={notes} title={'case.notes'} />
                        </FilterProvider>
                      )}
                    </Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon
                style={{ color: '#F4D07C', stroke: '#353535', strokeWidth: '10' }}
                icon={faKey}
              />
            </OverlayTrigger>
          </Col>
        )}
        {isRecycleBin && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="recycle-bin" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>{T.translate('case.recycleBin')}</Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#7b7b7bff' }} icon={faTrashAlt} />
            </OverlayTrigger>
          </Col>
        )}
        {isPrivate && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="private" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>{T.translate('case.isPrivateDocument')}</Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#6c757c' }} icon={faUserLock} />
            </OverlayTrigger>
          </Col>
        )}
        {hasAnnotations && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="annotations" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>
                      {T.translate('case.documentContainsAnnotations')}
                    </Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#EF8E43' }} icon={faStickyNote} />
            </OverlayTrigger>
          </Col>
        )}

        {hasPublicHyperlinks && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="publicHyperlinks" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>
                      {T.translate('case.documentContainsPublicHyperlinks')}
                    </Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#0288d1' }} icon={faLink} />
            </OverlayTrigger>
          </Col>
        )}
        {hasCrossRefs && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="hasCrossrefs" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>
                      {T.translate('case.documentContainsCrossRefs')}
                    </Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#0288d1' }} icon={faRetweet} />
            </OverlayTrigger>
          </Col>
        )}
        {source === 'merge' && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="merge" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>{T.translate('case.isMergedDoc')}</Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#6c757c' }} icon={faLayerGroup} />
            </OverlayTrigger>
          </Col>
        )}
        {duplicate && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="duplicate" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>{T.translate('case.duplicateFileMsg')}</Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <ContentCopyOutlinedIcon style={{ color: '#748CEC' }} fontSize="small" />
            </OverlayTrigger>
          </Col>
        )}
        {viewFlags && flags?.length > 0 && (
          <Col
            style={{
              paddingRight: '2px',
              paddingLeft: '2px',
              marginRight: '0.5rem',
              flexGrow: 0,
            }}
          >
            <OverlayTrigger
              placement="left"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="duplicate" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Title as="h4">{T.translate('case.flags')}</Popover.Title>
                    <Popover.Content>{flags}</Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: 'red' }} icon={faFlag} />
            </OverlayTrigger>
          </Col>
        )}
      </Row>
    );
  },
);

const TableActionsMemoized = React.memo(
  ({ row, handleShow, getFolderSpecificFileDetails, onRowSelect, onOpenViewDrawer }: any) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const isMenuOpen = Boolean(menuAnchorEl);
    const permissions = useSelector(selectPermissions);
    const hasExhibitNumber = useSelector(selectShowExhibitNumber);
    const enableRemovePages = useSelector(selectEnableRemovePages);
    const canUpdateExhibitReferences = useSelector(selectCanUpdateExhibitReferences);
    const canRemovePages = permissions?.documents?.canRemovePages;
    const canSplit = permissions?.documents?.canSplit;
    const { aiNotReady, rowHover = false, rowSelected = false } = row;
    const canSplitForRow = canSplit && canSplit(row);
    const canRemovePagesForRow = ((canRemovePages && canRemovePages(row)) ?? canSplitForRow) || false;
    const canShowRemovePagesAction =
      enableRemovePages && row?.source !== 'merge' && canRemovePagesForRow;
    const showRowActions = rowHover || rowSelected || isMenuOpen;

    const handleMenuClick = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setMenuAnchorEl(e.currentTarget);
    };

    const handleMenuClose = (e?: any) => {
      if (e) {
        e.stopPropagation();
      }
      setMenuAnchorEl(null);
    };

    const handleMenuItemClick = (action: string) => (e: any) => {
      e.stopPropagation();
      handleMenuClose();

      // Select the current row first
      if (onRowSelect) {
        onRowSelect([row]);
      }

      // Small delay to ensure row selection is processed
      setTimeout(() => {
        // Implement the actual handlers for each action
        switch (action) {
          case 'splitFile':
            if (handleShow) {
              handleShow(documentModalActions.split);
            }
            break;
          case 'removePages':
            if (!canShowRemovePagesAction) {
              return;
            }
            if (handleShow) {
              handleShow(documentModalActions.removePages);
            }
            break;
          case 'updateMeta':
            if (handleShow) {
              handleShow(documentModalActions.updateMeta);
            }
            break;
          case 'download':
            if (handleShow) {
              handleShow(documentModalActions.download);
            }
            break;
          case 'view':
            if (getFolderSpecificFileDetails) {
              getFolderSpecificFileDetails([row], 'normal');
            }
            break;
          case 'viewDrawer':
            if (onOpenViewDrawer) {
              onOpenViewDrawer(row);
            }
            break;
          case 'removeFrom':
            if (handleShow) {
              handleShow(documentModalActions.remove);
            }
            break;
          case 'takeReference':
            if (!hasExhibitNumber || !canUpdateExhibitReferences) {
              return;
            }
            if (handleShow) {
              handleShow(documentModalActions.takeReference, row);
            }
            break;
          case 'takeReferenceWithNames':
            if (!hasExhibitNumber || !canUpdateExhibitReferences) {
              return;
            }
            if (handleShow) {
              handleShow(documentModalActions.takeReferenceWithNames, row);
            }
            break;
          default:
            console.log('Action not implemented:', action);
        }
      }, 0);
    };

    const isDocFinder = window.location.href.includes('docFinder');
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          opacity: showRowActions ? 1 : 0,
          pointerEvents: showRowActions ? 'auto' : 'none',
          transition: 'opacity 120ms ease-in-out',
        }}
      >
        <Tooltip title={T.translate('generic.clickToOpenFileActionsMenu')} placement="left" arrow>
          <MoreVertIcon
            sx={{
              fontSize: '2.2rem',
              color: '#696969',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '50%',
              '&:hover': { color: '#7c94ec', backgroundColor: '#fff' },
            }}
            onClick={handleMenuClick}
          />
        </Tooltip>
        
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            style: { minWidth: 200 },
            onClick: (e: any) => e.stopPropagation(),
          }}
        >

          {!isDocFinder && hasExhibitNumber && canUpdateExhibitReferences && (
            <MenuItem
              onClick={handleMenuItemClick('takeReferenceWithNames')}
              disabled={!hasExhibitNumber}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faBookmark} style={{ fontSize: '1rem' }} />
              </ListItemIcon>
              {T.translate('generic.takeReferenceWithNames')}
            </MenuItem>
          )}
          {!isDocFinder && hasExhibitNumber && canUpdateExhibitReferences && (
            <MenuItem
              onClick={handleMenuItemClick('takeReference')}
              disabled={!hasExhibitNumber || !canUpdateExhibitReferences}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faBookmark} style={{ fontSize: '1rem' }} />
              </ListItemIcon>
              {T.translate('generic.takeReference')}
            </MenuItem>
          )}
          {!isDocFinder && onOpenViewDrawer && (
            <MenuItem onClick={handleMenuItemClick('viewDrawer')}>
              <ListItemIcon>
                <FilterCenterFocus fontSize="medium" style={{ padding: 0, marginLeft: -2 }} />
              </ListItemIcon>
              {T.translate('generic.focusView')}
            </MenuItem>
          )}
          <MenuItem onClick={handleMenuItemClick('view')}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '1rem' }} />
            </ListItemIcon>
            {T.translate('generic.openInNewTab')}
          </MenuItem>
          {permissions?.folders?.canDownloadDocuments && (
            <MenuItem onClick={handleMenuItemClick('download')}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faDownload} style={{ fontSize: '1rem' }} />
              </ListItemIcon>
              {T.translate('generic.download')}
            </MenuItem>
          )}
          {!isDocFinder && (
            <MenuItem onClick={handleMenuItemClick('updateMeta')}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faEdit} style={{ fontSize: '1rem' }} />
              </ListItemIcon>
              {T.translate('generic.updateMeta')}
            </MenuItem>
          )}
          <Tooltip
            title={aiNotReady ? T.translate('generic.aiNotReady') : ''}
            placement="left"
            arrow
          >
            <span>
              <MenuItem onClick={handleMenuItemClick('splitFile')}>
                <ListItemIcon>
                  <FontAwesomeIcon icon={faCut} style={{ fontSize: '1rem' }} />
                </ListItemIcon>
                {T.translate('generic.splitFile')}
              </MenuItem>
            </span>
          </Tooltip>
          {canShowRemovePagesAction && (
            <MenuItem onClick={handleMenuItemClick('removePages')}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faMinusCircle} style={{ fontSize: '1rem' }} />
              </ListItemIcon>
              {T.translate('generic.removePages')}
            </MenuItem>
          )}
          <MenuItem onClick={handleMenuItemClick('removeFrom')}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '1rem' }} />
            </ListItemIcon>
            {T.translate('generic.removeFrom')}
          </MenuItem>
        </Menu>
      </div>
    );
  },
);

TableInfoMemoized.displayName = 'TableInfo';
TableActionsMemoized.displayName = 'TableActions';

export { TableActionsMemoized as TableActions };
export default TableInfoMemoized;
